import type { FolderNode, SidebarFolder, SidebarWorker } from "./types";

/** Nests the flat folder list under its parents, each level sorted by name. */
export function buildFolderTree(folders: SidebarFolder[]): FolderNode[] {
  const nodes = new Map<string, FolderNode>(
    folders.map((f) => [f.id, { ...f, children: [] }])
  );
  const roots: FolderNode[] = [];
  for (const node of nodes.values()) {
    const parent = node.parentId ? nodes.get(node.parentId) : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }
  const sortRec = (list: FolderNode[]) => {
    list.sort((a, b) => a.name.localeCompare(b.name));
    for (const n of list) sortRec(n.children);
  };
  sortRec(roots);
  return roots;
}

/** Workers keyed by folder id; the "" key holds those without a known folder. */
export function groupWorkersByFolder(
  workers: SidebarWorker[],
  folders: SidebarFolder[]
): Map<string, SidebarWorker[]> {
  const known = new Set(folders.map((f) => f.id));
  const map = new Map<string, SidebarWorker[]>();
  for (const w of workers) {
    const key = w.folderId && known.has(w.folderId) ? w.folderId : "";
    const list = map.get(key) ?? [];
    list.push(w);
    map.set(key, list);
  }
  return map;
}

/** Open requests need action even when an older snapshot has no classified counts. */
export function workerAttentionCounts(worker: SidebarWorker) {
  return worker.attentionCounts ?? { errors: 0, opportunities: worker.openResourceCount, neutral: 0 };
}

/** Current worker priorities across a folder and all nested folders. */
export function subtreeAttentionCounts(
  node: FolderNode,
  workersByFolder: Map<string, SidebarWorker[]>
): NonNullable<SidebarWorker["attentionCounts"]> {
  const counts = { errors: 0, opportunities: 0, neutral: 0 };
  const children = node.children.map(child => subtreeAttentionCounts(child, workersByFolder));
  for (const next of [...(workersByFolder.get(node.id) ?? []).map(workerAttentionCounts), ...children]) {
    counts.errors += next.errors;
    counts.opportunities += next.opportunities;
    counts.neutral += next.neutral;
  }
  return counts;
}
